import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useLanguage } from '../i18n/LanguageContext';

// MUI Icons
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import MessageIcon from "@mui/icons-material/Message";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ComputerIcon from "@mui/icons-material/Computer";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CallIcon from "@mui/icons-material/Call";
import SpaIcon from "@mui/icons-material/Spa";

import imageee from "../imaged/contact.jpg";

export default function ContactUs() {
  const { t } = useLanguage();
  return (
    <Box sx={{ fontFamily: "sans-serif", overflowX: 'hidden' }}>
      {/* ================= HERO SECTION ================= */}
      <Box
        sx={{
          position: "relative",
          height: "85vh",
          minHeight: 600,
          backgroundImage: `url("${imageee}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.8))"
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, textAlign: "center" }}>
          <Typography variant="overline" sx={{ color: "#81c784", letterSpacing: 4, fontWeight: 700, fontSize: "1.1rem", mb: 2, display: "block" }}>
            {t('contact.hero.overline')}
          </Typography>
          <Typography variant="h1" sx={{ color: "#fff", fontWeight: 800, mb: 3, fontSize: { xs: '3rem', md: '5.5rem' } }}>
            {t('contact.title')}
          </Typography>
          <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.95)", fontSize: "1.4rem", fontWeight: 300 }}>
            {t('contact.hero.subtitle')}
          </Typography>
        </Container>
      </Box>

      {/* ================= CONSULTATION OPTIONS ================= */}
      <Container sx={{ py: 8 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold", color: "#2e7d32" }}
        >
          {t('contact.consultation.title')}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 4,
            mt: 4,
            alignItems: "stretch",
          }}
        >
          {/* ONLINE CARD */}
          <Card
            sx={{
              flex: "1 1 300px",
              p: 4,
              textAlign: "center",
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              borderRadius: 6,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              border: '1px solid rgba(0,0,0,0.04)',
              "&:hover": {
                transform: "translateY(-12px)",
                boxShadow: '0 20px 40px rgba(46, 125, 50, 0.15)',
                backgroundColor: "#ffffff",
                "& .MuiSvgIcon-root": { transform: 'scale(1.1)' },
                "& .action-btn": { backgroundColor: "#1b5e20", transform: 'scale(1.05)' }
              },
            }}
          >
            <Box sx={{ 
              fontSize: 60, 
              color: "#2e7d32", 
              mb: 3,
              display: 'flex',
              justifyContent: 'center',
              "& .MuiSvgIcon-root": { transition: 'transform 0.3s ease' }
            }}>
              <ComputerIcon fontSize="inherit" />
            </Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 800, color: "#1b5e20", letterSpacing: -0.5 }}
            >
              {t('contact.consultation.online.title')}
            </Typography>
            <Typography sx={{ color: "text.secondary", mb: 4, flexGrow: 1, lineHeight: 1.6 }}>
              {t('contact.consultation.online.text')}
            </Typography>
            <Button
              className="action-btn"
              variant="contained"
              sx={{
                backgroundColor: "#2e7d32",
                borderRadius: 50,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                boxShadow: '0 8px 16px rgba(46, 125, 50, 0.2)',
                transition: 'all 0.3s ease',
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
            >
              {t('contact.consultation.bookNow')}
            </Button>
          </Card>

          {/* IN-PERSON CARD */}
          <Card
            sx={{
              flex: "1 1 300px",
              p: 4,
              textAlign: "center",
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              borderRadius: 6,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              border: '1px solid rgba(0,0,0,0.04)',
              "&:hover": {
                transform: "translateY(-12px)",
                boxShadow: '0 20px 40px rgba(46, 125, 50, 0.15)',
                backgroundColor: "#ffffff",
                "& .MuiSvgIcon-root": { transform: 'scale(1.1)' },
                "& .action-btn": { backgroundColor: "#1b5e20", transform: 'scale(1.05)' }
              },
            }}
          >
            <Box sx={{ 
              fontSize: 60, 
              color: "#2e7d32", 
              mb: 3,
              display: 'flex',
              justifyContent: 'center',
              "& .MuiSvgIcon-root": { transition: 'transform 0.3s ease' }
            }}>
              <LocalHospitalIcon fontSize="inherit" />
            </Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 800, color: "#1b5e20", letterSpacing: -0.5 }}
            >
              {t('contact.consultation.clinic.title')}
            </Typography>
            <Typography sx={{ color: "text.secondary", mb: 4, flexGrow: 1, lineHeight: 1.6 }}>
              {t('contact.consultation.clinic.text')}
            </Typography>
            <Button
              className="action-btn"
              variant="contained"
              sx={{
                backgroundColor: "#2e7d32",
                borderRadius: 50,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                boxShadow: '0 8px 16px rgba(46, 125, 50, 0.2)',
                transition: 'all 0.3s ease',
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
            >
              {t('contact.consultation.bookNow')}
            </Button>
          </Card>

          {/* PHONE CARD */}
          <Card
            sx={{
              flex: "1 1 300px",
              p: 3,
              textAlign: "center",
              boxShadow: 4,
              borderRadius: 4,
              transition: "all 0.3s ease",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: 8,
                backgroundColor: "#2e7d32",
                color: "#fff",
                "& .MuiTypography-root": { color: "#fff" },
              },
            }}
          >
            <Box sx={{ fontSize: 50, color: "#2e7d32", mb: 1 }}>
              <CallIcon fontSize="inherit" />
            </Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#1b5e20" }}
            >
              {t('contact.consultation.phone.title')}
            </Typography>
            <Typography sx={{ color: "text.secondary", mb: 2, flexGrow: 1 }}>
              {t('contact.consultation.phone.text')}
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#2e7d32",
                borderRadius: 2,
                px: 3,
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
            >
              {t('contact.consultation.bookNow')}
            </Button>
          </Card>
        </Box>
      </Container>

      {/* ================= CONSULTATION DETAILS ================= */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={2} alignItems="center">
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#2e7d32", mb: 3 }}
            >
              {t('contact.onlineDetails.title')}
            </Typography>

            <Typography
              variant="h5"
              sx={{ mb: 3, color: "text.secondary", lineHeight: 1.7 }}
            >
              {t('contact.onlineDetails.text1')}
            </Typography>

            <Typography sx={{ mb: 4, lineHeight: 1.7 }}>
              {t('contact.onlineDetails.text2')}
            </Typography>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {(Array.isArray(t('contact.onlineDetails.list')) ? t('contact.onlineDetails.list') : []).map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: 2,
                    borderRadius: 3,
                    boxShadow: 2,
                    backgroundColor: "#f9fdf8",
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
                  }}
                >
                  <Box sx={{ fontSize: 28, color: "#2e7d32", mr: 2 }}>
                    <SpaIcon fontSize="inherit" />
                  </Box>
                  <Typography sx={{ lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ================= CONTACT FORM ================= */}
      <Container sx={{ py: 8 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold", color: "#2e7d32", mb: 2 }}
        >
          {t('contact.form.title')}
        </Typography>

        <Typography
          align="center"
          sx={{ maxWidth: "600px", mx: "auto", mb: 5, color: "text.secondary" }}
        >
          {t('contact.form.subtitle')}
        </Typography>

        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {/* LOCATION CARD */}
          <Grid item xs={12} md={5} sx={{ display: "flex" }}>
            <Box
              sx={{
                flex: 1,
                p: 4,
                borderRadius: 4,
                boxShadow: 4,
                background: "#fff",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 440,
                width: 400,
              }}
            >
              <LocationOnIcon sx={{ fontSize: 48, color: "#2e7d32", mb: 1 }} />
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {t('contact.form.locationTitle')}
              </Typography>
              <Typography sx={{ color: "text.secondary", mb: 3 }}>
                {t('contact.form.locationAddress')}
              </Typography>

              <Box
                component="iframe"
                title="Paritranam Location"
                src="https://www.google.com/maps?q=17.6294292,74.798539&z=17&hl=en&output=embed"
                sx={{
                  width: "100%",
                  height: 220,
                  border: 0,
                  borderRadius: 3,
                  boxShadow: 2,
                }}
              />
              <Button
                component="a"
                href="https://maps.google.com/maps?q=17.6294292%2C74.798539&z=17&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="primary"
                sx={{ mt: 2, borderRadius: 50, px: 3, py: 0.8 }}
              >
                Open in Google Maps
              </Button>
            </Box>
          </Grid>

          {/* FORM CARD */}
          <Grid item xs={12} md={7} sx={{ display: "flex" }}>
            <Box
              sx={{
                flex: 1,
                p: 5,
                borderRadius: 4,
                boxShadow: 4,
                background: "#fff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 440,
                width: 400,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: "#2e7d32", mb: 5, textAlign: "center" }}
              >
                {t('contact.form.messageTitle')}
              </Typography>

              <Box
                component="form"
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                <TextField
                  label={t('contact.labels.name')}
                  variant="outlined"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: "#2e7d32" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
                <TextField
                  label={t('contact.labels.email')}
                  variant="outlined"
                  type="email"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#2e7d32" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
                <TextField
                  label={t('contact.labels.phone')}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: "#2e7d32" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
                <TextField
                  label={t('contact.labels.message')}
                  variant="outlined"
                  multiline
                  rows={3}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MessageIcon
                          sx={{ color: "#2e7d32", alignSelf: "flex-start", mt: 1 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />

                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      backgroundColor: "#2e7d32",
                      borderRadius: 3,
                      py: 1.2,
                      fontWeight: "bold",
                      "&:hover": { backgroundColor: "#1b5e20" },
                    }}
                  >
                    {t('contact.form.submit')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
